import * as React from 'react';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectProps } from '@mui/material/Select';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

/**
 * A selectable option. Either an `{ id, name }` pair, or a bare primitive used
 * for both the value and the label.
 */
type MSelectChoice = { id: string | number; name: string } | string | number;

/**
 * Props for {@link MSelect}.
 */
interface MSelectProps extends Omit<SelectProps, 'defaultValue' | 'value' | 'onChange'> {
    /** Renders a leading `<em>None</em>` option. Defaults to `true`. */
    isEmptyOption?: boolean;
    /** Field label rendered above/inside the select. */
    label?: string;
    /** Options rendered as `MenuItem`s. */
    choices: MSelectChoice[];
    /** Pre-selected value applied on first render. */
    defaultValue?: string | number;
    /** Controlled value. When provided, the component becomes controlled. */
    value?: string | number;
    /**
     * Called with the newly selected value.
     *
     * @param value - The selected option id.
     */
    onChange: (value: string | number) => void;
    /** Lower-cases option values before rendering/matching. Defaults to `false`. */
    ignoreCaseSensitive?: boolean;
    /** Number of rows (of {@link ITEM_HEIGHT}px each) visible before the menu scrolls. Defaults to `4.5`. */
    paperHeight?: number;
}

/**
 * A MUI {@link Select} wrapper offering a consistent `choices` / `value` /
 * `defaultValue` / `onChange` contract, with an optional leading empty option.
 */
const MSelect: React.FC<MSelectProps> = ({
    isEmptyOption = true,
    variant = 'filled',
    label,
    choices,
    defaultValue,
    value,
    onChange,
    ignoreCaseSensitive = false,
    ...props
}) => {
    const [mValue, setMValue] = React.useState<string | number>('');
    const [mDefaultValue, setDefaultValue] = React.useState<string | number>('');

    React.useEffect(() => {
        if (defaultValue !== undefined) {
            setDefaultValue(defaultValue);
            setMValue(defaultValue);
        }
    }, [defaultValue]);

    React.useEffect(() => {
        if (value !== undefined) setMValue(value);
    }, [value]);

    const handleChange: SelectProps['onChange'] = (e) => {
        const { value } = e.target;
        setMValue(value as string | number);
        onChange(value as string | number);
    };

    return (
        <FormControl variant={variant} sx={{ minWidth: '100%' }} size="small">
            <InputLabel id="demo-select-small-label">{label}</InputLabel>
            <Select
                {...props}
                labelId="demo-select-small"
                id="demo-select-small"
                defaultValue={mDefaultValue}
                value={mValue}
                label={label}
                MenuProps={{
                    className: props.className ?? '',
                    slotProps: {
                        paper: {
                            style: {
                                maxHeight: ITEM_HEIGHT * (props.paperHeight || 4.5) + ITEM_PADDING_TOP
                            }
                        }
                    }
                }}
                onChange={handleChange as any}

            >
                {isEmptyOption ? <MenuItem key={-1} value="" ><em>None</em></MenuItem> : null}
                {choices.map((m, index) => {
                    const choice = m as { id?: string | number; name?: string };
                    let itemValue: string | number = choice.id ?? (m as string | number);
                    if (ignoreCaseSensitive && typeof itemValue === 'string') itemValue = itemValue.toLowerCase();
                    return <MenuItem key={index} id={String(itemValue)} value={itemValue}>{choice.name ?? (m as any)}</MenuItem>;
                })}
            </Select>
        </FormControl>
    );
};

export default React.memo(MSelect);
